import { z } from 'zod';
import { MemberRole } from '@/core/constants/roles';
import { zPlannedDate } from '@/shared/lib/datetime/datetime.zod';

export const updateTaskSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'tasks:validation.titleRequired' })
    .max(200, { error: 'tasks:validation.titleMax' })
    .optional(),
  description: z.string().optional(),
  priority: z.enum(['urgent', 'high', 'medium', 'low']).optional(),
  statusId: z.string().optional(),
  dueDate: zPlannedDate().nullable().optional(),
  assigneeId: z.string().nullable().optional(),
  estimatedMinutes: z.number().int().min(0).nullable().optional(),
});

export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const copyTaskSchema = z.object({
  includeTags: z.boolean(),
  includeFieldValues: z.boolean(),
  includeChecklists: z.boolean(),
  includeSubtasks: z.boolean(),
});

export type CopyTaskInput = z.infer<typeof copyTaskSchema>;

export const bulkEditTasksSchema = z
  .object({
    statusName: z.string().optional(),
    groupName: z.string().optional(),
    assigneeEmail: z
      .union([z.email({ error: 'tasks:bulkEdit.validation.invalidEmail' }), z.literal('')])
      .optional(),
    clearAssignee: z.boolean().optional(),
    priority: z.string().optional(),
    // Bulk edit sets a date-only value via the shared date picker (ISO string);
    // it is mapped to a date-only PlannedDate when the payload is built.
    dueDate: z.string().nullable().optional(),
    startDate: z.string().nullable().optional(),
  })
  .refine((data) => !data.startDate || !data.dueDate || data.startDate <= data.dueDate, {
    error: 'tasks:bulkEdit.validation.dateRangeInvalid',
    path: ['dueDate'],
  });

export type BulkEditTasksInput = z.infer<typeof bulkEditTasksSchema>;

export const addProjectMemberSchema = z.object({
  userId: z.string().min(1, { error: 'tasks:header.addMember.validation.userRequired' }),
  role: z.enum([MemberRole.Admin, MemberRole.Collaborator, MemberRole.Client]),
});

export type AddProjectMemberInput = z.infer<typeof addProjectMemberSchema>;

export const createSubtaskSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'tasks:validation.subtaskNameRequired' })
    .max(200, { error: 'tasks:validation.subtaskNameMax' }),
  assigneeId: z.string().nullable().optional(),
  priority: z.enum(['urgent', 'high', 'medium', 'low']).optional(),
  dueDate: zPlannedDate().nullable().optional(),
});

export type CreateSubtaskInput = z.infer<typeof createSubtaskSchema>;
